import { Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudEditorParentService, CrudEditorSelfService, requiredValidator, UserStorage, Warning } from '@scisys-uim/uim-common';
import { Asset, AssetType, BUSINESS_UNIT_TYPE, Firearm, FirearmAssignmentLog, validateFirearm, validateFirearmAbleToGoIntoService } from 'assets-model';
import { AssetService, AssetTypeService, FirearmAssignmentLogService, FirearmService, FirearmStorageLocationService, RefCodeStorage } from 'assets-service';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AutocompleteStringFilterFunctionAsync } from '../../../shared/form/autocomplete.util';
import { FormCustomiser } from '../../../shared/form/form-customiser';
import { AssetItemComponent, assetItemDefaultFormExcluding } from '../../asset/editor/asset-item.component';
import { ASSET_TYPE_FIREARM_ASSET_NAME, FIREARM_STORAGE_LOCATION_RANGER_HOME } from '../../domain-constants';
import { FirearmItemComponentBase } from './firearm-item.component.base';

const ACQUIRED_DETAILS_FIELDS = ['manufacturer', 'model', 'identityReference', 'purchaseDate', 'purchasePrice'];

@Component({
  selector: 'app-firearm-item',
  templateUrl: './firearm-item.component.html',
  styleUrls: ['./firearm-item.component.scss'],
  providers: [CrudEditorParentService],
  viewProviders: [CrudEditorSelfService]
})
export class FirearmItemComponent extends FirearmItemComponentBase {
  firearmAssignmentLog: FirearmAssignmentLog;

  assetInputFormDisplay: string[][] = assetItemDefaultFormExcluding(["updatedCreatedStats", "aspects", "tags", "status"])

  locationSelectionListFilterLoad: AutocompleteStringFilterFunctionAsync = (text?: string) => {
    if (this.item?.asset?.businessUnit == null) {
      return of(null);
    }
    var showRangerHome = true
    if (text == null || text.length == 0) {
      text = null;
      showRangerHome = true;
    } else {
      showRangerHome = FIREARM_STORAGE_LOCATION_RANGER_HOME.toLowerCase().includes(text.toLowerCase());
    }
    return this.firearmStorageLocationService.findByFullFiltersExtended({
      includeNational: true,
      buId: this.item?.asset?.businessUnit?.buId,
      text: text
    }, null, true, true).pipe(map(response => {
      var locations = [...response.items.map(item => item.location)]
      if (showRangerHome) {
        locations.push(FIREARM_STORAGE_LOCATION_RANGER_HOME);
      }
      return locations;
    }));
  }

  buStorageLocations: string[];

  initialAssetFormCustomiser: FormCustomiser = (form: FormGroup) => {
    form.controls.status.setValidators([]);
    var requiredAfterAcquisition = !this.createMode && this.item?.lifecycleState != 'PENDING';
    var descriptionValidators: ValidatorFn[] = [];
    if (requiredAfterAcquisition) {
      descriptionValidators.push(requiredValidator);
    }
    form.controls.description.setValidators(descriptionValidators);
    form.controls.description.updateValueAndValidity({ emitEvent: false });

    if (this.createMode) {
      form.controls.manufacturer.setValidators([requiredValidator]);
      form.controls.model.setValidators([requiredValidator]);
      form.controls.manufacturer.updateValueAndValidity({ emitEvent: false });
      form.controls.model.updateValueAndValidity({ emitEvent: false });
    }

    var locationValidators: ValidatorFn[] = [(control) => {
        if (control.value == null) {
          return null;
        }
        if (this.buStorageLocations == null) {
          return null;
        }
        if (this.buStorageLocations.indexOf(control.value) < 0) {
          return {
            'invalidLocation': "'" + control.value + "' is not a valid Firearm Storage Location for "
              + BUSINESS_UNIT_TYPE.description(this.item?.asset?.businessUnit) + "."
          };
        }
        return null;
      }];
    if (requiredAfterAcquisition) {
      locationValidators.push(requiredValidator);
    }
    form.controls.location.setValidators(locationValidators);
    form.controls.location.updateValueAndValidity({ emitEvent: false });
  }

  fieldDefaultsAssetFormsCustomiser: FormCustomiser = (form: FormGroup) => {
    form.controls.assetType.disable();
    if (this.acquiredDetailsLocked()) {
      ACQUIRED_DETAILS_FIELDS.forEach(field => form.controls[field]?.disable());
    }
  }

  @ViewChild(AssetItemComponent) assetItem: AssetItemComponent;

  constructor(protected firearmService: FirearmService,
    private firearmAssignmentLogService: FirearmAssignmentLogService,
    protected assetTypeService: AssetTypeService,
    protected assetService: AssetService,
    protected firearmStorageLocationService: FirearmStorageLocationService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(firearmService, assetService, refCodeStorage, fb, elementRef, router, route, userStorage, dialog, injector, snackbar);
  }

  // propagate warnings/form-errors/peristence-enabled/markFormsTouched to/from child components
  isDelegateToChildComponents(): boolean {
    return true;
  }

  createNewItem() {
    return new Firearm({
      asset: new Asset({
        assetType: new AssetType({ name: 'Firearm Asset' })
      })
    });
  }

  createForm(): FormGroup {
    var formGroup = super.createForm();
    formGroup.removeControl('assetId');
    formGroup.removeControl('asset');
    return formGroup;
  }

  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
    this.calibreLoadSelectionList();
    this.firearmTypeLoadSelectionList();
    this.lifecycleStateLoadSelectionList();
    this.currentAssignmentFirearmAssignmentTypeLoadSelectionList();
    this.currentAssignmentFirearmAssignmentStorageTypeLoadSelectionList();
  }

  get hasPendingAcquisitionActions(): boolean {
    return this.firearmAssignmentLog?.assignments.filter(a => a.storageType == 'ACQUIRE' && a.actionPending).length > 0;
  }

  itemSet() {
    super.itemSet();

    this.loadValidLocations();

    this.item?.asset.propertyChanges$.subscribe((event) => {
      if (event.property == 'businessUnit') {
        this.loadValidLocations();
      }
    });

    if (this.item?.asset != null) {
      this.assetTypeService.findByName(ASSET_TYPE_FIREARM_ASSET_NAME).subscribe(
        data => {
          this.item.asset.assetType = data;
          this.item.asset.initAspectsForAssetType(this.createMode, data);
        }
      )
    }

    if (this.item != null && !this.createMode) {
      this.firearmAssignmentLogService.get(this.item?.assetId).subscribe(log => {
        this.firearmAssignmentLog = log;
      });
    }

    this.addAllWarnings();
  }

  itemValidationErrors(): string[] {
    // cache if performance is an issue, and set cached variable to null on changing anything that could affect the calculated warnings - e.g. in businessUnitChanged
    const validationWarnings = (this.hasPendingAcquisitionActions ? validateFirearmAbleToGoIntoService(this.item) : validateFirearm(this.item)) || {};
    const messages: string[] = Object.values(validationWarnings);
    return messages;
  }

  getActiveWarnings(): Warning[] {
    var warnings = [...super.getActiveWarnings()];

    for (var message of this.itemValidationErrors()) {
      warnings.push({ message: message, active() { return true; } });
    }

    return warnings;
  }

  addAllWarnings(): void {
    super.addAllWarnings();
    this.addWarningDynamic(() => "There are " + this.firearmAssignmentLog.pendingActionsCount + " pending assignment actions for this firearm. Some operations are restricted until actions are completed.", () => this.firearmAssignmentLog?.pendingActions);
  }

  setDefaultDisabledFields(): void {
    super.setDefaultDisabledFields();
    if (!this.form) {
      return;
    }
    this.suspended?.disable();
    this.personalFirearm?.disable();
    if (this.acquiredDetailsLocked()) {
      this.form.get('firearmType')?.disable();
      this.form.get('calibre')?.disable();
    }
  }

  private acquiredDetailsLocked(): boolean {
    return !this.createMode && this.item?.lifecycleState != 'PENDING'
      && !this.userStorage.currentUserHasAnyAuthority('EDIT_ACQUIRED_FIREARM_DETAILS');
  }

  storageTypeHiddenClass() {
    if (this.item?.currentAssignmentFirearmAssignmentType != 'STORAGE') {
      return {'storage-type-hidden': true}
    }
  }

  private loadValidLocations() {
    this.locationSelectionListFilterLoad().subscribe((locations) => {
      if (locations != null) {
        this.buStorageLocations = [...locations, FIREARM_STORAGE_LOCATION_RANGER_HOME];
      } else {
        this.buStorageLocations = [];
      }
      this.assetItem?.locationSelectionListFilter(this.item.asset.location);
      this.assetItem?.form.controls?.location?.updateValueAndValidity();
    });
  }
}





